import {
  endpoints,
  IBanner,
  IBankDetail,
  ICartItem,
  ICategory,
  ICollection,
  ICoupon,
  ICourier,
  ICreateCartItem,
  ICreatePaymentOrderBody,
  ICreatePaymentOrderResponse,
  IGetAllSubSubCategoryQueryDto,
  IGetPaginatedOrdersQuery,
  IAddBankDetailInput,
  IAddProductInput,
  IAddSupportRequestInput,
  IAddUserAddressInput,
  IApplyCouponBody,
  IApplyCouponResponse,
  IOrder,
  IProduct,
  IProductReview,
  IProductVariant,
  ISellerStatistics,
  ISeller,
  ISubSubCategory,
  ISupport,
  IUser,
  IUserAddress,
  IVerifyPaymentResponse,
  IWishlist,
  IWalletContext,
  IWithdrawalRequest,
  IPaginatedResponse,
  IPaginationOptions,
  IReleaseReservationBody,
  IUpdateProfileInput,
  IDeleteUserInput,
  OrderStatus,
  SearchResponse,
} from "muminsmart-utils";
import { baseApi } from "./baseApi";

export const apiSlice = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getHomeSubSubCategories: builder.query<
      ISubSubCategory[],
      IGetAllSubSubCategoryQueryDto | void
    >({
      query: (params) => ({
        url: endpoints.subSubCategory.index,
        params: params ?? {
          showOnHomeScreen: true,
          sortBy: "homeScreenDisplayOrder",
          sortOrder: "ASC",
        },
      }),
      providesTags: [{ type: "Home", id: "SUB_SUB_CATEGORIES" }],
      keepUnusedDataFor: 3600,
    }),

    getBanners: builder.query<IBanner[], void>({
      query: () => ({ url: endpoints.banner.index }),
      providesTags: [{ type: "Home", id: "BANNERS" }],
      keepUnusedDataFor: 3600,
    }),

    getCollections: builder.query<ICollection[], void>({
      query: () => ({
        url: endpoints.collection.index,
        params: {
          isActive: true,
          sortBy: "displayOrder",
          sortOrder: "ASC",
        },
      }),
      providesTags: [{ type: "Home", id: "COLLECTIONS" }],
      keepUnusedDataFor: 3600,
    }),

    getCategories: builder.query<ICategory[], Record<string, unknown> | void>({
      query: (params) => ({
        url: endpoints.category.index,
        params: params ?? {
          isActive: true,
          relations: "subCategories,subCategories.subSubCategories",
        },
      }),
      providesTags: [{ type: "Category", id: "LIST" }],
      serializeQueryArgs: ({ queryArgs }) =>
        `categories-${JSON.stringify(queryArgs ?? {})}`,
      keepUnusedDataFor: 3600,
    }),

    getSeller: builder.query<ISeller, string>({
      query: (id) => ({ url: endpoints.seller.id(id) }),
      providesTags: (_result, _error, id) => [{ type: "Seller", id }],
    }),

    getSubSubCategory: builder.query<ISubSubCategory, string>({
      query: (id) => ({ url: endpoints.subSubCategory.id(id) }),
      providesTags: (_result, _error, id) => [{ type: "Category", id }],
    }),

    getProduct: builder.query<IProduct, string>({
      query: (id) => ({ url: endpoints.product.id(id) }),
      providesTags: (_result, _error, id) => [{ type: "Product", id }],
    }),

    getUserProfile: builder.query<IUser, void>({
      query: () => ({ url: endpoints.user.profile }),
      providesTags: [{ type: "Profile", id: "CURRENT" }],
    }),

    getWalletContexts: builder.query<IWalletContext, void>({
      query: () => ({ url: endpoints.wallet.contextsMe }),
      providesTags: [{ type: "Wallet", id: "CONTEXTS" }],
    }),

    getSellerStatistics: builder.query<ISellerStatistics, void>({
      query: () => ({ url: endpoints.seller.statistics }),
      providesTags: [{ type: "Seller", id: "STATISTICS" }],
    }),

    getSellerOrders: builder.query<
      IPaginatedResponse<IOrder>,
      IGetPaginatedOrdersQuery | void
    >({
      query: (params) => ({
        url: endpoints.order.sellerOrders,
        params,
      }),
      providesTags: [{ type: "Order", id: "SELLER_RECENT" }],
    }),

    getCartItems: builder.query<ICartItem[], void>({
      query: () => ({ url: endpoints.cartItem.index }),
      providesTags: [{ type: "Cart", id: "LIST" }],
    }),

    getWishlistItems: builder.query<IWishlist[], void>({
      query: () => ({ url: endpoints.wishlist.index }),
      providesTags: [{ type: "Wishlist", id: "LIST" }],
    }),

    getUserAddresses: builder.query<IUserAddress[], void>({
      query: () => ({ url: endpoints.userAddress.index }),
      providesTags: [{ type: "Profile", id: "ADDRESSES" }],
    }),

    getCouriers: builder.query<ICourier[], void>({
      query: () => ({ url: endpoints.courier.index }),
      providesTags: [{ type: "Order", id: "COURIERS" }],
      keepUnusedDataFor: 3600,
    }),

    getValidCoupons: builder.query<ICoupon[], void>({
      query: () => ({
        url: endpoints.coupon.index,
        params: { valid: true, showInCart: true },
      }),
      providesTags: [{ type: "Cart", id: "COUPONS" }],
    }),

    getProductReviews: builder.query<
      IProductReview[],
      { productId: string; isActive?: boolean }
    >({
      query: ({ productId, isActive = true }) => ({
        url: endpoints.productReview.index,
        params: { productId, isActive },
      }),
      providesTags: (_result, _error, { productId }) => [
        { type: "Product", id: `REVIEWS-${productId}` },
      ],
      keepUnusedDataFor: 600,
    }),

    getSearchResults: builder.query<
      SearchResponse,
      { search: string; limit?: number }
    >({
      query: ({ search, limit = 6 }) => ({
        url: endpoints.search.index,
        params: { search, limit },
      }),
      keepUnusedDataFor: 120,
    }),

    getPaymentStatus: builder.query<
      IVerifyPaymentResponse,
      { paymentAttemptId: string; orderId: string }
    >({
      query: (params) => ({
        url: endpoints.payment.status,
        params,
      }),
    }),

    createProduct: builder.mutation<IProduct, IAddProductInput>({
      query: (data) => ({
        url: endpoints.product.index,
        method: "POST",
        data,
      }),
      invalidatesTags: [
        { type: "Product", id: "LIST" },
        { type: "Paginated", id: endpoints.product.paginated },
        { type: "Paginated", id: endpoints.productVariant.paginated },
      ],
    }),

    updateProduct: builder.mutation<
      IProduct,
      { id: string; data: Partial<IProduct> | IAddProductInput }
    >({
      query: ({ id, data }) => ({
        url: endpoints.product.id(id),
        method: "PUT",
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Product", id },
        { type: "Product", id: "LIST" },
        { type: "Paginated", id: endpoints.product.paginated },
        { type: "Paginated", id: endpoints.productVariant.paginated },
      ],
    }),

    updateProductVariant: builder.mutation<
      IProductVariant,
      { id: string; data: Partial<IProductVariant> }
    >({
      query: ({ id, data }) => ({
        url: endpoints.productVariant.id(id),
        method: "PUT",
        data,
      }),
      invalidatesTags: [
        { type: "Product" },
        { type: "Paginated", id: endpoints.productVariant.paginated },
        { type: "Paginated", id: endpoints.product.paginated },
      ],
    }),

    createProductReview: builder.mutation<
      IProductReview,
      {
        productId: string;
        rating: number;
        title?: string;
        message?: string;
      }
    >({
      query: (body) => ({
        url: endpoints.productReview.index,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { productId }) => [
        { type: "Product", id: `REVIEWS-${productId}` },
        { type: "Product", id: productId },
      ],
    }),

    createSupportRequest: builder.mutation<ISupport, IAddSupportRequestInput>({
      query: (data) => ({
        url: endpoints.support.index,
        method: "POST",
        data,
      }),
      invalidatesTags: [
        { type: "Support", id: "LIST" },
        { type: "Paginated", id: endpoints.support.paginated },
        { type: "Paginated", id: endpoints.support.myRequests },
      ],
    }),

    updateUserProfile: builder.mutation<
      Pick<IUser, "fullName" | "phoneNumber" | "its" | "profileImageUrl">,
      IUpdateProfileInput
    >({
      query: (data) => ({
        url: endpoints.user.profile,
        method: "PUT",
        data,
      }),
      invalidatesTags: [{ type: "Profile", id: "CURRENT" }],
    }),

    registerPushToken: builder.mutation<
      { message: string },
      { token: string; platform: "ios" | "android" }
    >({
      query: (data) => ({
        url: endpoints.user.pushToken,
        method: "PUT",
        data,
      }),
    }),

    deleteAccount: builder.mutation<{ message: string }, IDeleteUserInput>({
      query: (data) => ({
        url: endpoints.user.account,
        method: "DELETE",
        data,
      }),
    }),

    updateOrderStatus: builder.mutation<
      IOrder,
      { id: string; status: OrderStatus }
    >({
      query: ({ id, status }) => ({
        url: endpoints.order.id(id),
        method: "PUT",
        data: { status },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Order", id },
        { type: "Order", id: "LIST" },
        { type: "Order", id: "SELLER_RECENT" },
        { type: "Paginated", id: endpoints.order.myOrders },
        { type: "Paginated", id: endpoints.order.sellerOrders },
      ],
    }),

    updateOrderTracking: builder.mutation<
      IOrder,
      { id: string; courierName: string; trackingNumber: string }
    >({
      query: ({ id, courierName, trackingNumber }) => ({
        url: endpoints.order.id(id),
        method: "PUT",
        data: { courierName, trackingNumber },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Order", id },
        { type: "Order", id: "LIST" },
        { type: "Order", id: "SELLER_RECENT" },
        { type: "Paginated", id: endpoints.order.myOrders },
        { type: "Paginated", id: endpoints.order.sellerOrders },
      ],
    }),

    createUserAddress: builder.mutation<IUserAddress, IAddUserAddressInput>({
      query: (data) => ({
        url: endpoints.userAddress.index,
        method: "POST",
        data,
      }),
      invalidatesTags: [{ type: "Profile", id: "ADDRESSES" }],
    }),

    updateUserAddress: builder.mutation<
      IUserAddress,
      { id: string; data: IAddUserAddressInput }
    >({
      query: ({ id, data }) => ({
        url: endpoints.userAddress.id(id),
        method: "PATCH",
        data,
      }),
      invalidatesTags: [{ type: "Profile", id: "ADDRESSES" }],
    }),

    deleteUserAddress: builder.mutation<null, string>({
      query: (id) => ({
        url: endpoints.userAddress.id(id),
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Profile", id: "ADDRESSES" }],
    }),

    applyCoupon: builder.mutation<IApplyCouponResponse, IApplyCouponBody>({
      query: (data) => ({
        url: endpoints.coupon.apply,
        method: "POST",
        data,
      }),
      invalidatesTags: [{ type: "Cart", id: "COUPONS" }],
    }),

    createPaymentOrder: builder.mutation<
      ICreatePaymentOrderResponse,
      ICreatePaymentOrderBody
    >({
      query: (data) => ({
        url: endpoints.payment.createOrder,
        method: "POST",
        data,
      }),
      invalidatesTags: [
        { type: "Cart", id: "LIST" },
        { type: "Order", id: "LIST" },
        { type: "Order", id: "SELLER_RECENT" },
        { type: "Seller", id: "STATISTICS" },
        { type: "Paginated", id: endpoints.order.myOrders },
        { type: "Paginated", id: endpoints.order.sellerOrders },
      ],
    }),

    releasePaymentReservation: builder.mutation<
      unknown,
      IReleaseReservationBody
    >({
      query: (data) => ({
        url: endpoints.payment.releaseReservation,
        method: "POST",
        data,
      }),
    }),

    createBankDetail: builder.mutation<
      IBankDetail,
      { walletId: string; data: IAddBankDetailInput }
    >({
      query: ({ walletId, data }) => ({
        url: endpoints.wallet.bankDetails(walletId),
        method: "POST",
        data,
      }),
      invalidatesTags: [{ type: "Wallet", id: "CONTEXTS" }],
    }),

    createWalletWithdrawal: builder.mutation<
      IWithdrawalRequest,
      { walletId: string; amount: number; description: string }
    >({
      query: ({ walletId, amount, description }) => ({
        url: endpoints.wallet.withdraw(walletId),
        method: "POST",
        data: { amount, description },
      }),
      invalidatesTags: [{ type: "Wallet", id: "CONTEXTS" }],
    }),

    addCartItem: builder.mutation<ICartItem, ICreateCartItem>({
      query: (body) => ({
        url: endpoints.cartItem.index,
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "Cart", id: "LIST" },
        { type: "Cart", id: "COUPONS" },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            apiSlice.util.updateQueryData(
              "getCartItems",
              undefined,
              (draft) => {
                const existingIndex = draft.findIndex(
                  (item) => item.productVariantId === data.productVariantId,
                );

                if (existingIndex === -1) {
                  draft.push(data);
                  return;
                }

                draft[existingIndex] = data;
              },
            ),
          );
        } catch {
          // Error toast handled by the caller.
        }
      },
    }),

    updateCartItem: builder.mutation<
      ICartItem,
      { id: string; quantity: number }
    >({
      query: ({ id, quantity }) => ({
        url: endpoints.cartItem.id(id),
        method: "PATCH",
        data: { quantity },
      }),
      invalidatesTags: [
        { type: "Cart", id: "LIST" },
        { type: "Cart", id: "COUPONS" },
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            apiSlice.util.updateQueryData(
              "getCartItems",
              undefined,
              (draft) => {
                const existingIndex = draft.findIndex((item) => item.id === id);

                if (existingIndex === -1) {
                  draft.push(data);
                  return;
                }

                draft[existingIndex] = data;
              },
            ),
          );
        } catch {
          // Error toast handled by the caller.
        }
      },
    }),

    removeCartItem: builder.mutation<null, string>({
      query: (id) => ({
        url: endpoints.cartItem.id(id),
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "Cart", id: "LIST" },
        { type: "Cart", id: "COUPONS" },
      ],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          apiSlice.util.updateQueryData("getCartItems", undefined, (draft) =>
            draft.filter((item) => item.id !== id),
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    addWishlistItem: builder.mutation<
      IWishlist,
      { productId: string; productVariantId: string }
    >({
      query: (body) => ({
        url: endpoints.wishlist.index,
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "Wishlist", id: "LIST" },
        { type: "FetchAll", id: endpoints.wishlist.index },
        { type: "Paginated", id: endpoints.wishlist.paginated },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            apiSlice.util.updateQueryData(
              "getWishlistItems",
              undefined,
              (draft) => {
                const existingIndex = draft.findIndex(
                  (item) => item.productVariantId === data.productVariantId,
                );

                if (existingIndex === -1) {
                  draft.push(data);
                  return;
                }

                draft[existingIndex] = data;
              },
            ),
          );
        } catch {
          // Error toast handled by the caller.
        }
      },
    }),

    removeWishlistItem: builder.mutation<null, string>({
      query: (id) => ({
        url: endpoints.wishlist.id(id),
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "Wishlist", id: "LIST" },
        { type: "FetchAll", id: endpoints.wishlist.index },
        { type: "Paginated", id: endpoints.wishlist.paginated },
      ],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          apiSlice.util.updateQueryData(
            "getWishlistItems",
            undefined,
            (draft) => draft.filter((item) => item.id !== id),
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    fetchPaginated: builder.query<
      IPaginatedResponse<unknown>,
      { endpoint: string; params: IPaginationOptions & Record<string, unknown> }
    >({
      query: ({ endpoint, params }) => ({ url: endpoint, params }),
      providesTags: (_result, _error, { endpoint }) => [
        { type: "Paginated", id: endpoint },
      ],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { page: _page, ...restParams } = queryArgs.params;
        return `${endpointName}-${queryArgs.endpoint}-${JSON.stringify(restParams)}`;
      },
      merge: (currentCache, newItems, { arg }) => {
        const page = Number(arg.params.page) || 1;

        if (page <= 1) {
          return newItems;
        }

        return {
          ...newItems,
          items: [...(currentCache?.items ?? []), ...(newItems.items ?? [])],
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!currentArg || !previousArg) {
          return true;
        }

        const {
          page: currentPage,
          search: currentSearch,
          ...currentRest
        } = currentArg.params;
        const {
          page: _previousPage,
          search: previousSearch,
          ...previousRest
        } = previousArg.params;

        if (currentSearch !== previousSearch) {
          return true;
        }

        return (
          currentPage !== _previousPage ||
          JSON.stringify(currentRest) !== JSON.stringify(previousRest)
        );
      },
    }),

    fetchAll: builder.query<
      unknown[],
      { endpoint: string; params?: Record<string, unknown> }
    >({
      query: ({ endpoint, params }) => ({ url: endpoint, params }),
      providesTags: (_result, _error, { endpoint }) => [
        { type: "FetchAll", id: endpoint },
      ],
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}-${queryArgs.endpoint}-${JSON.stringify(queryArgs.params ?? {})}`,
    }),

    fetchSingle: builder.query<
      unknown,
      { endpoint: string; params?: Record<string, unknown> }
    >({
      query: ({ endpoint, params }) => ({ url: endpoint, params }),
      providesTags: (_result, _error, { endpoint }) => [
        { type: "FetchSingle", id: endpoint },
      ],
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}-${queryArgs.endpoint}-${JSON.stringify(queryArgs.params ?? {})}`,
    }),
  }),
});

export const {
  useGetHomeSubSubCategoriesQuery,
  useGetBannersQuery,
  useGetCollectionsQuery,
  useGetCategoriesQuery,
  useGetSellerQuery,
  useGetSubSubCategoryQuery,
  useGetProductQuery,
  useGetUserProfileQuery,
  useGetWalletContextsQuery,
  useGetSellerStatisticsQuery,
  useGetSellerOrdersQuery,
  useGetCartItemsQuery,
  useGetWishlistItemsQuery,
  useGetUserAddressesQuery,
  useGetCouriersQuery,
  useGetValidCouponsQuery,
  useLazyGetValidCouponsQuery,
  useGetProductReviewsQuery,
  useLazyGetSearchResultsQuery,
  useGetPaymentStatusQuery,
  useLazyGetPaymentStatusQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useUpdateProductVariantMutation,
  useCreateProductReviewMutation,
  useCreateSupportRequestMutation,
  useUpdateUserProfileMutation,
  useRegisterPushTokenMutation,
  useDeleteAccountMutation,
  useUpdateOrderStatusMutation,
  useUpdateOrderTrackingMutation,
  useCreateUserAddressMutation,
  useUpdateUserAddressMutation,
  useDeleteUserAddressMutation,
  useApplyCouponMutation,
  useCreatePaymentOrderMutation,
  useReleasePaymentReservationMutation,
  useCreateBankDetailMutation,
  useCreateWalletWithdrawalMutation,
  useAddCartItemMutation,
  useUpdateCartItemMutation,
  useRemoveCartItemMutation,
  useAddWishlistItemMutation,
  useRemoveWishlistItemMutation,
  useFetchPaginatedQuery,
  useLazyFetchPaginatedQuery,
  useFetchAllQuery,
  useLazyFetchAllQuery,
  useFetchSingleQuery,
  useLazyFetchSingleQuery,
} = apiSlice;
